<template>
    <v-container fluid>
        <v-progress-linear
            v-if="loading"
            class="mb-8"
            color="primary"
            indeterminate
        />

        <validation-observer ref="form" v-slot="{ invalid }">
            <v-form @submit.prevent="submit">
                <section v-if="!loading">
                    <v-row>
                        <v-col cols="12" md="4">
                            <validation-provider
                                v-slot="{ errors }"
                                vid="Obras"
                                name="Obras"
                                rules="required"
                            >
                                <v-autocomplete
                                    v-model="fields.obra_id"
                                    :disabled="method === 'update'"
                                    :items="obras"
                                    :item-text="item => item.Codigo + ' - ' + item.Descricao"
                                    item-value="Id"
                                    :error-messages="errors"
                                    label="Obras *"
                                    outlined
                                    dense
                                    @change="changeWork(true)"
                                />
                            </validation-provider>
                        </v-col>

                        <v-col v-if="showContent" cols="12" md="4">
                            <v-autocomplete
                                v-model="fields.user_id"
                                :items="funcionarios"
                                :item-text="item => item.funcionario_codigo + ' - ' + item.name"
                                item-value="id"
                                label="Atribuir a funcionário"
                                outlined
                                dense
                                clearable
                            />
                        </v-col>
                    </v-row>

                    <v-container v-if="showContent" fluid>
                        <v-tabs
                            v-model="tab"
                            dark
                            fixed-tabs
                            background-color="primary"
                            icons-and-text
                        >
                            <v-tab>
                                Mão Obra
                                <v-icon>mdi-account-hard-hat</v-icon>
                            </v-tab>
                            <v-tab>
                                Equipamento
                                <v-icon>mdi-bulldozer</v-icon>
                            </v-tab>
                            <v-tab>
                                Material
                                <v-icon>mdi-archive</v-icon>
                            </v-tab>
                            <v-tab>
                                Subempreitadas
                                <v-icon>mdi-briefcase</v-icon>
                            </v-tab>
                        </v-tabs>

                        <v-tabs-items v-model="tab">
                            <v-tab-item>
                                <v-container fluid>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-card class="elevation-0">
                                                <v-card-title class="warning white--text">
                                                    <span>Mão Obra Interna</span>
                                                    <v-spacer />
                                                    <v-btn
                                                        small
                                                        color="primary"
                                                        dark
                                                        :loading="internalCatalogLoading && !internalCatalogLoaded"
                                                        @click="openMoDialog('internal')"
                                                    >
                                                        <v-icon left small>mdi-plus</v-icon>
                                                        Adicionar
                                                    </v-btn>
                                                </v-card-title>

                                                <v-card-text class="pa-0">
                                                    <v-simple-table fixed-header>
                                                        <template v-slot:default>
                                                            <thead>
                                                                <tr>
                                                                    <th>Nome</th>
                                                                    <th>Frente Obra</th>
                                                                    <th>Classe</th>
                                                                    <th>HN</th>
                                                                    <th>HExt</th>
                                                                    <th>Local</th>
                                                                    <th class="text-right">Opções</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr
                                                                    v-for="(item, pageIndex) in paginatedFuncionariosInternos"
                                                                    :key="rowKey(item, pageIndex, 'int')"
                                                                >
                                                                    <td>{{ internalWorkerText(item) }}</td>
                                                                    <td>{{ frenteText(item.frente_id) }}</td>
                                                                    <td>{{ classeText(item) }}</td>
                                                                    <td>{{ valueOrDash(item.hn) }}</td>
                                                                    <td>{{ valueOrDash(item.hext) }}</td>
                                                                    <td>{{ valueOrDash(item.local) }}</td>
                                                                    <td class="text-right text-no-wrap">
                                                                        <v-btn
                                                                            icon
                                                                            small
                                                                            color="primary"
                                                                            @click="openMoDialog('internal', realInternalIndex(pageIndex))"
                                                                        >
                                                                            <v-icon small>mdi-pencil</v-icon>
                                                                        </v-btn>
                                                                        <v-btn
                                                                            icon
                                                                            small
                                                                            color="error"
                                                                            @click="deleteFuncionarioInterno(realInternalIndex(pageIndex))"
                                                                        >
                                                                            <v-icon small>mdi-delete</v-icon>
                                                                        </v-btn>
                                                                    </td>
                                                                </tr>
                                                                <tr v-if="fields.funcionariosInternos.length === 0">
                                                                    <td colspan="7" class="text-center grey--text py-6">
                                                                        Sem mão de obra interna.
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </template>
                                                    </v-simple-table>

                                                    <div
                                                        v-if="internalTotalPages > 1"
                                                        class="d-flex align-center justify-center py-4"
                                                    >
                                                        <v-btn
                                                            fab
                                                            x-small
                                                            color="primary"
                                                            dark
                                                            :disabled="internalPage <= 1"
                                                            @click="internalPage--"
                                                        >
                                                            <v-icon small>mdi-chevron-left</v-icon>
                                                        </v-btn>
                                                        <span class="mx-4">
                                                            {{ internalPage }} / {{ internalTotalPages }}
                                                        </span>
                                                        <v-btn
                                                            fab
                                                            x-small
                                                            color="primary"
                                                            dark
                                                            :disabled="internalPage >= internalTotalPages"
                                                            @click="internalPage++"
                                                        >
                                                            <v-icon small>mdi-chevron-right</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </v-card-text>
                                            </v-card>
                                        </v-col>

                                        <v-col cols="12">
                                            <v-card class="elevation-0">
                                                <v-card-title class="warning white--text">
                                                    <span>Mão Obra Externa</span>
                                                    <v-spacer />
                                                    <v-btn
                                                        small
                                                        color="primary"
                                                        dark
                                                        class="mr-2"
                                                        @click="openEntitySearchDialog"
                                                    >
                                                        <v-icon left small>mdi-account-search</v-icon>
                                                        Pesquisar por entidade
                                                    </v-btn>
                                                    <v-btn
                                                        small
                                                        color="primary"
                                                        dark
                                                        @click="openMoDialog('external')"
                                                    >
                                                        <v-icon left small>mdi-plus</v-icon>
                                                        Adicionar
                                                    </v-btn>
                                                </v-card-title>

                                                <v-card-text class="pa-0">
                                                    <v-simple-table fixed-header>
                                                        <template v-slot:default>
                                                            <thead>
                                                                <tr>
                                                                    <th>Entidade</th>
                                                                    <th>Nome</th>
                                                                    <th>Frente Obra</th>
                                                                    <th>Classe</th>
                                                                    <th>HN</th>
                                                                    <th>HExt</th>
                                                                    <th>Local</th>
                                                                    <th class="text-right">Opções</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr
                                                                    v-for="(item, pageIndex) in paginatedFuncionariosExternos"
                                                                    :key="rowKey(item, pageIndex, 'ext')"
                                                                >
                                                                    <td>{{ entityText(item.entidade) }}</td>
                                                                    <td>{{ externalWorkerText(item) }}</td>
                                                                    <td>{{ frenteText(item.frente_id) }}</td>
                                                                    <td>{{ classeText(item) }}</td>
                                                                    <td>{{ valueOrDash(item.hn) }}</td>
                                                                    <td>{{ valueOrDash(item.hext) }}</td>
                                                                    <td>{{ valueOrDash(item.local) }}</td>
                                                                    <td class="text-right text-no-wrap">
                                                                        <v-btn
                                                                            icon
                                                                            small
                                                                            color="primary"
                                                                            @click="openMoDialog('external', realExternalIndex(pageIndex))"
                                                                        >
                                                                            <v-icon small>mdi-pencil</v-icon>
                                                                        </v-btn>
                                                                        <v-btn
                                                                            icon
                                                                            small
                                                                            color="error"
                                                                            @click="deleteFuncionarioExterno(realExternalIndex(pageIndex))"
                                                                        >
                                                                            <v-icon small>mdi-delete</v-icon>
                                                                        </v-btn>
                                                                    </td>
                                                                </tr>
                                                                <tr v-if="fields.funcionariosExternos.length === 0">
                                                                    <td colspan="8" class="text-center grey--text py-6">
                                                                        Sem mão de obra externa.
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </template>
                                                    </v-simple-table>

                                                    <div
                                                        v-if="externalTotalPages > 1"
                                                        class="d-flex align-center justify-center py-4"
                                                    >
                                                        <v-btn
                                                            fab
                                                            x-small
                                                            color="primary"
                                                            dark
                                                            :disabled="externalPage <= 1"
                                                            @click="externalPage--"
                                                        >
                                                            <v-icon small>mdi-chevron-left</v-icon>
                                                        </v-btn>
                                                        <span class="mx-4">
                                                            {{ externalPage }} / {{ externalTotalPages }}
                                                        </span>
                                                        <v-btn
                                                            fab
                                                            x-small
                                                            color="primary"
                                                            dark
                                                            :disabled="externalPage >= externalTotalPages"
                                                            @click="externalPage++"
                                                        >
                                                            <v-icon small>mdi-chevron-right</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </v-card-text>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-tab-item>

                            <v-tab-item>
                                <Equipments
                                    :fields="fields"
                                    :frentes_obra="frentes_obra"
                                    :hide-frentes="hideFrentes"
                                    :template_classes="classes"
                                />
                            </v-tab-item>

                            <v-tab-item>
                                <Materials
                                    :fields="fields"
                                    :frentes_obra="frentes_obra"
                                    :hide-frentes="hideFrentes"
                                    :template_classes="classes"
                                />
                            </v-tab-item>

                            <v-tab-item>
                                <Subcontractors :fields="fields" />
                            </v-tab-item>
                        </v-tabs-items>
                    </v-container>

                    <v-row v-if="showContent" class="mt-8">
                        <v-col cols="12" class="text-center">
                            <v-btn
                                type="submit"
                                color="success"
                                large
                                :loading="saving"
                                :disabled="invalid || saving"
                            >
                                <v-icon left>mdi-content-save</v-icon>
                                Gravar
                            </v-btn>
                        </v-col>
                    </v-row>
                </section>
            </v-form>
        </validation-observer>

        <v-dialog v-model="moDialog" max-width="850" persistent>
            <v-card>
                <v-card-title class="primary white--text">
                    {{ moDialogTitle }}
                </v-card-title>

                <v-card-text class="pt-6">
                    <v-progress-linear
                        v-if="loadingMoCatalogs"
                        class="mb-5"
                        color="primary"
                        indeterminate
                    />

                    <v-alert v-if="moDialogError" type="error" dense text>
                        {{ moDialogError }}
                    </v-alert>

                    <v-row>
                        <template v-if="moType === 'external'">
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="moDraft.entidade"
                                    :items="entidades"
                                    :item-text="item => item.Codigo + ' - ' + item.Nome"
                                    label="Entidade *"
                                    outlined
                                    dense
                                    return-object
                                    @change="onMoEntityChange"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="moDraft.funcionario"
                                    :items="moExternalWorkers"
                                    :item-text="externalCatalogWorkerText"
                                    item-value="ID"
                                    label="Funcionário *"
                                    outlined
                                    dense
                                    :disabled="!moDraft.entidade || loadingExternalWorkers"
                                    :loading="loadingExternalWorkers"
                                />
                            </v-col>
                        </template>

                        <v-col v-else cols="12">
                            <v-autocomplete
                                v-model="moDraft.funcionario"
                                :items="funcionariosInternos"
                                :item-text="item => item.Codigo + ' - ' + item.Nome"
                                item-value="Codigo"
                                label="Funcionário *"
                                outlined
                                dense
                                :loading="internalCatalogLoading"
                                :disabled="internalCatalogLoading"
                            />
                        </v-col>

                        <v-col v-if="!hideFrentes" cols="12" md="6">
                            <v-autocomplete
                                v-model="moDraft.frente_id"
                                :items="frentes_obra"
                                :item-text="item => item.Descricao"
                                item-value="ID"
                                label="Frente de Obra *"
                                outlined
                                dense
                                @change="onMoFrenteChange"
                            />
                        </v-col>

                        <v-col v-if="moClasseNeeded" cols="12" md="6">
                            <v-autocomplete
                                v-model="moDraft.classe_id"
                                :items="moAvailableClasses"
                                :item-text="item => item.Descricao"
                                item-value="ID"
                                label="Classe *"
                                outlined
                                dense
                            />
                        </v-col>

                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="moDraft.hn"
                                label="HN"
                                type="number"
                                step="0.01"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="moDraft.hext"
                                label="HExt"
                                type="number"
                                step="0.01"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="moDraft.local"
                                label="Local"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="closeMoDialog">Cancelar</v-btn>
                    <v-btn color="primary" dark :loading="savingMoLine" @click="saveMoLine">
                        Gravar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Pesquisa/adicao em lote de mao de obra externa por entidade.
             O catalogo de entidades e os colaboradores so sao carregados quando esta modal abre. -->
        <v-dialog v-model="entitySearchDialog" fullscreen persistent>
            <v-card>
                <v-toolbar dark color="primary">
                    <v-toolbar-title>Funcionários por entidade</v-toolbar-title>
                    <v-spacer />
                    <v-btn icon dark @click="closeEntitySearchDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-card-text class="pt-6">
                    <v-progress-linear
                        v-if="loadingEntitySearchCatalogs"
                        color="primary"
                        indeterminate
                        class="mb-5"
                    />

                    <v-alert v-if="entitySearchError" type="error" dense text>
                        {{ entitySearchError }}
                    </v-alert>

                    <v-row>
                        <v-col v-if="!hideFrentes" cols="12" md="4">
                            <v-autocomplete
                                v-model="entitySearchFrente"
                                :items="frentes_obra"
                                :item-text="item => item.Descricao"
                                item-value="ID"
                                label="Frente de Obra *"
                                outlined
                                dense
                                clearable
                                @change="onEntitySearchFrenteChange"
                            />
                        </v-col>

                        <v-col cols="12" :md="hideFrentes ? 6 : 4">
                            <v-autocomplete
                                v-model="entitySearchClasse"
                                :items="entitySearchClasses"
                                :item-text="item => item.Descricao"
                                item-value="ID"
                                label="Classe"
                                outlined
                                dense
                                clearable
                                :loading="loadingEntitySearchCatalogs"
                                :disabled="loadingEntitySearchCatalogs || entitySearchClasses.length === 0"
                            />
                        </v-col>

                        <v-col cols="12" :md="hideFrentes ? 6 : 4">
                            <v-autocomplete
                                v-model="entitySearchEntity"
                                :items="entidades"
                                :item-text="item => item.Codigo + ' - ' + item.Nome"
                                item-value="ID"
                                label="Entidade *"
                                outlined
                                dense
                                clearable
                                :loading="loadingEntitySearchWorkers"
                                @change="onEntitySearchEntityChange"
                            />
                        </v-col>

                        <v-col cols="12" md="8">
                            <v-text-field
                                v-model="entitySearchTerm"
                                label="Pesquisar por nome ou número"
                                prepend-inner-icon="mdi-magnify"
                                outlined
                                dense
                                clearable
                                :disabled="!entitySearchEntity"
                            />
                        </v-col>

                        <v-col cols="12" md="4" class="d-flex align-center">
                            <v-btn
                                text
                                color="primary"
                                :disabled="filteredEntitySearchWorkers.length === 0"
                                @click="toggleAllEntitySearchWorkers"
                            >
                                <v-icon left small>mdi-checkbox-multiple-marked-outline</v-icon>
                                {{ allVisibleEntityWorkersSelected ? 'Desmarcar visíveis' : 'Selecionar visíveis' }}
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-alert
                        v-if="entitySearchEntity && !loadingEntitySearchWorkers && entitySearchWorkers.length === 0"
                        type="info"
                        dense
                        text
                    >
                        Não existem colaboradores disponíveis para esta entidade.
                    </v-alert>

                    <v-progress-linear
                        v-if="loadingEntitySearchWorkers"
                        color="primary"
                        indeterminate
                        class="mb-4"
                    />

                    <div v-if="!loadingEntitySearchWorkers && filteredEntitySearchWorkers.length > 0">
                        <div class="d-flex align-center mb-3">
                            <strong>{{ filteredEntitySearchWorkers.length }} colaborador(es)</strong>
                            <v-spacer />
                            <span class="grey--text text--darken-1">{{ entitySelectedCount }} selecionado(s)</span>
                        </div>

                        <v-virtual-scroll
                            :items="filteredEntitySearchWorkers"
                            :height="entitySearchListHeight"
                            item-height="82"
                        >
                            <template v-slot:default="{ item }">
                                <v-list-item class="px-2">
                                    <v-list-item-action class="mr-3">
                                        <v-checkbox v-model="item.selected" color="primary" />
                                    </v-list-item-action>

                                    <v-list-item-content>
                                        <v-row dense align="center">
                                            <v-col cols="12" md="6">
                                                <div class="font-weight-medium">
                                                    {{ item.numero }} - {{ item.nome }}
                                                </div>
                                                <div class="caption grey--text">
                                                    {{ entityText(item.entidade) }}
                                                </div>
                                            </v-col>

                                            <v-col cols="4" md="2">
                                                <v-text-field
                                                    v-model="item.hn"
                                                    label="HN"
                                                    type="number"
                                                    step="0.01"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    @focus="item.selected = true"
                                                />
                                            </v-col>
                                            <v-col cols="4" md="2">
                                                <v-text-field
                                                    v-model="item.hext"
                                                    label="HExt"
                                                    type="number"
                                                    step="0.01"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    @focus="item.selected = true"
                                                />
                                            </v-col>
                                            <v-col cols="4" md="2">
                                                <v-text-field
                                                    v-model="item.local"
                                                    label="Local"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    @focus="item.selected = true"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider />
                            </template>
                        </v-virtual-scroll>
                    </div>
                </v-card-text>

                <v-card-actions class="px-6 pb-6">
                    <v-spacer />
                    <v-btn text @click="closeEntitySearchDialog">Cancelar</v-btn>
                    <v-btn
                        color="success"
                        dark
                        :disabled="entitySelectedCount === 0"
                        @click="addEntitySearchWorkers"
                    >
                        <v-icon left small>mdi-account-multiple-plus</v-icon>
                        Adicionar {{ entitySelectedCount }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="success" color="success" timeout="2500">
            Template gravado com sucesso.
        </v-snackbar>

        <v-snackbar v-model="error" color="error" timeout="5000">
            {{ error_message || 'Ocorreu um erro ao gravar o template.' }}
        </v-snackbar>
    </v-container>
</template>

<script>
import dayjs from 'dayjs'
import Obra from '@/api/Obra.js'
import Entidades from '@/api/Entidades.js'
import FrenteObra from '@/api/FrenteObra.js'
import Classe from '@/api/Classe.js'
import Template from '@/api/Templates.js'
import Works from '@/api/Works.js'
import Equipments from '@/components/Templates/objects/Equipments.vue'
import Materials from '@/components/Templates/objects/Materials.vue'
import Subcontractors from '@/components/Templates/objects/Subcontractors.vue'

export default {
    name: 'Template',

    components: {
        Equipments,
        Materials,
        Subcontractors
    },

    props: {
        method: {
            type: String,
            required: true
        },
        template: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            loading: true,
            saving: false,
            success: false,
            error: false,
            error_message: null,

            tab: 0,
            obras: [],
            funcionarios: [],
            funcionariosInternos: [],
            entidades: [],
            frentes_obra: [],
            classes: [],
            hideFrentes: false,
            showContent: false,

            internalPage: 1,
            internalPerPage: 10,
            externalPage: 1,
            externalPerPage: 10,

            moDialog: false,
            moType: 'internal',
            moEditIndex: null,
            moDraft: {},
            moAvailableClasses: [],
            moClasseNeeded: false,
            moExternalWorkers: [],
            loadingMoCatalogs: false,
            loadingExternalWorkers: false,
            savingMoLine: false,
            moDialogError: null,

            internalCatalogLoaded: false,
            internalCatalogLoading: false,
            internalCatalogPromise: null,
            entitiesCatalogLoaded: false,

            entitySearchDialog: false,
            entitySearchEntity: null,
            entitySearchFrente: null,
            entitySearchClasse: null,
            entitySearchClasses: [],
            entitySearchTerm: '',
            entitySearchWorkers: [],
            loadingEntitySearchCatalogs: false,
            loadingEntitySearchWorkers: false,
            entitySearchError: null,

            fields: this.emptyFields()
        }
    },

    computed: {
        internalTotalPages() {
            return Math.max(1, Math.ceil(this.fields.funcionariosInternos.length / this.internalPerPage))
        },

        externalTotalPages() {
            return Math.max(1, Math.ceil(this.fields.funcionariosExternos.length / this.externalPerPage))
        },

        paginatedFuncionariosInternos() {
            const start = (this.internalPage - 1) * this.internalPerPage
            return this.fields.funcionariosInternos.slice(start, start + this.internalPerPage)
        },

        paginatedFuncionariosExternos() {
            const start = (this.externalPage - 1) * this.externalPerPage
            return this.fields.funcionariosExternos.slice(start, start + this.externalPerPage)
        },

        moDialogTitle() {
            const action = this.moEditIndex === null ? 'Adicionar' : 'Editar'
            return `${action} ${this.moType === 'internal' ? 'Mão de Obra Interna' : 'Mão de Obra Externa'}`
        },

        filteredEntitySearchWorkers() {
            const term = String(this.entitySearchTerm || '').trim().toLowerCase()
            if (!term) return this.entitySearchWorkers

            return this.entitySearchWorkers.filter(item => {
                const haystack = `${item.numero || ''} ${item.nome || ''}`.toLowerCase()
                return haystack.includes(term)
            })
        },

        entitySelectedCount() {
            return this.entitySearchWorkers.filter(item => item.selected).length
        },

        allVisibleEntityWorkersSelected() {
            return this.filteredEntitySearchWorkers.length > 0
                && this.filteredEntitySearchWorkers.every(item => item.selected)
        },

        entitySearchListHeight() {
            if (typeof window === 'undefined') return 520
            return Math.max(300, Math.min(620, window.innerHeight - 360))
        }
    },

    watch: {
        template: {
            immediate: true,
            handler(value) {
                if (!value || !value.template) return
                this.fields = this.normaliseTemplate(value.template)
            }
        },

        internalTotalPages(value) {
            if (this.internalPage > value) this.internalPage = value
        },

        externalTotalPages(value) {
            if (this.externalPage > value) this.externalPage = value
        }
    },

    async mounted() {
        await this.fillBaseData()

        // O catálogo de funcionários internos é pesado.
        // Começamos a carregá-lo em background assim que a página está pronta,
        // sem bloquear a abertura do template.
        this.preloadInternalCatalog()
    },

    methods: {
        emptyFields() {
            return {
                obra_id: null,
                frente_id: null,
                classe_id: null,
                user_id: null,
                funcionariosInternos: [],
                funcionariosExternos: [],
                equipamentosInternos: [],
                equipamentosExternos: [],
                materiais: [],
                subempreitadas: [],
                obsMO: null,
                obsEQ: null
            }
        },

        normaliseTemplate(template) {
            const fields = Object.assign(this.emptyFields(), template || {})

            fields.funcionariosInternos = (fields.funcionariosInternos || []).map(item => ({
                ...item,
                hn: this.formatNumber(item.hn),
                hext: this.formatNumber(item.hext)
            }))

            fields.funcionariosExternos = (fields.funcionariosExternos || []).map(item => ({
                ...item,
                hn: this.formatNumber(item.hn),
                hext: this.formatNumber(item.hext)
            }))

            fields.equipamentosInternos = fields.equipamentosInternos || []
            fields.equipamentosExternos = fields.equipamentosExternos || []
            fields.materiais = fields.materiais || []
            fields.subempreitadas = fields.subempreitadas || []

            return fields
        },

        formatNumber(value) {
            if (value === null || value === undefined || value === '') return 0
            const number = Number(value)
            if (Number.isNaN(number)) return value
            return Number.isInteger(number) ? number : Number(number.toFixed(2))
        },

        async fillBaseData() {
            try {
                const response = await Obra.all({ tipo: 'O' })
                this.obras = response.data || []

                if (this.method === 'update' && this.fields.obra_id) {
                    await this.changeWork(false)
                }
            } finally {
                this.loading = false
            }
        },

        async changeWork(resetLines = false) {
            if (!this.fields.obra_id) {
                this.showContent = false
                return
            }

            if (resetLines && this.method === 'create') {
                this.fields.funcionariosInternos = []
                this.fields.funcionariosExternos = []
                this.fields.equipamentosInternos = []
                this.fields.equipamentosExternos = []
                this.fields.materiais = []
                this.fields.subempreitadas = []
            }

            // O catálogo de funcionários internos não depende da obra, por isso
            // mantemo-lo em cache quando a obra muda.
            this.entitiesCatalogLoaded = false
            this.entidades = []
            this.frentes_obra = []
            this.classes = []
            this.hideFrentes = false

            const selectedWork = this.obras.find(item => String(item.Id) === String(this.fields.obra_id))
            const obraPai = selectedWork ? selectedWork.Codigo : null

            /*
             * Existem dois tipos de configuração no ODC:
             *
             * 1) Obra com Frentes -> Classes
             * 2) Obra sem necessidade de Frente -> Classes diretamente da obra
             *
             * Por isso carregamos SEMPRE as classes diretas da obra.
             * Se existirem classes diretas, a frente deixa de ser obrigatória e
             * nem sequer é mostrada nas modais.
             */
            const requests = [
                FrenteObra.all(obraPai),
                Works.getUsers(this.fields.obra_id),
                Classe.getClassesByObraPai(this.fields.obra_id)
            ]

            const [
                frentesResponse,
                usersResponse,
                directClassesResponse
            ] = await Promise.all(requests)

            this.frentes_obra = frentesResponse.data || []
            this.funcionarios = usersResponse.data || []
            this.classes = directClassesResponse.data || []

            /*
             * Regra correta:
             *
             * - Se a obra TEM frentes de obra, mostramos sempre a Frente e a
             *   Classe só é escolhida depois da frente.
             * - Se a obra NÃO TEM frentes, escondemos a Frente e usamos
             *   diretamente as classes da obra.
             *
             * Algumas obras podem devolver classes diretas e, ao mesmo tempo,
             * ter frentes configuradas. Nesses casos a existência de frentes
             * tem prioridade.
             */
            this.hideFrentes = this.frentes_obra.length === 0

            this.showContent = true
        },

        preloadInternalCatalog() {
            // Fire-and-forget: se falhar, não impedimos a página de abrir.
            // Ao carregar em "Adicionar" tentamos novamente e mostramos o loading.
            this.ensureInternalCatalog().catch(() => {})
        },

        async ensureInternalCatalog() {
            if (this.internalCatalogLoaded) {
                return this.funcionariosInternos
            }

            // Se o preload já estiver em curso, reutilizamos exatamente o mesmo pedido
            // em vez de lançar uma segunda chamada ao backend.
            if (this.internalCatalogPromise) {
                return this.internalCatalogPromise
            }

            this.internalCatalogLoading = true

            this.internalCatalogPromise = Classe.getClasseFuncionariosTemplate()
                .then(response => {
                    this.funcionariosInternos = response.data || []
                    this.internalCatalogLoaded = true
                    return this.funcionariosInternos
                })
                .finally(() => {
                    this.internalCatalogLoading = false
                    this.internalCatalogPromise = null
                })

            return this.internalCatalogPromise
        },

        async ensureEntitiesCatalog() {
            if (this.entitiesCatalogLoaded) return
            const response = await Entidades.withWorkers(this.fields.obra_id)
            this.entidades = response.data && response.data.entidades
                ? response.data.entidades
                : (response.data || [])
            this.entitiesCatalogLoaded = true
        },

        async openEntitySearchDialog() {
            this.entitySearchError = null
            this.entitySearchTerm = ''
            this.entitySearchWorkers = []
            this.entitySearchEntity = null
            this.entitySearchFrente = null
            this.entitySearchClasse = null
            this.entitySearchClasses = []
            this.loadingEntitySearchCatalogs = true
            this.entitySearchDialog = true

            try {
                await Promise.all([
                    this.ensureEntitiesCatalog(),
                    this.ensureEntitySearchClasses()
                ])
            } catch (error) {
                console.error(error)
                this.entitySearchError = 'Não foi possível carregar os dados da pesquisa por entidade.'
            } finally {
                this.loadingEntitySearchCatalogs = false
            }
        },

        async ensureEntitySearchClasses() {
            /*
             * Obras sem frente trabalham diretamente com as classes da obra.
             * A pesquisa por entidade não pode depender de escolher uma frente
             * nesses casos.
             */
            if (this.hideFrentes) {
                if (Array.isArray(this.classes) && this.classes.length > 0) {
                    this.entitySearchClasses = [...this.classes]
                    return this.entitySearchClasses
                }

                const response = await Classe.getClassesByObraPai(this.fields.obra_id)
                this.classes = response.data || []
                this.entitySearchClasses = [...this.classes]

                return this.entitySearchClasses
            }

            this.entitySearchClasses = []
            return this.entitySearchClasses
        },

        closeEntitySearchDialog() {
            this.entitySearchDialog = false
            this.entitySearchEntity = null
            this.entitySearchFrente = null
            this.entitySearchClasse = null
            this.entitySearchClasses = []
            this.entitySearchTerm = ''
            this.entitySearchWorkers = []
            this.entitySearchError = null
        },

        async onEntitySearchEntityChange() {
            this.entitySearchTerm = ''
            this.entitySearchWorkers = []
            this.entitySearchError = null

            const entity = this.entitySearchEntity
            if (!entity) return

            // A mesma entidade e o mesmo endpoint usados na janela Adicionar.
            const entityId = typeof entity === 'object'
                ? (entity.ID || entity.id)
                : entity
            if (!entityId) return

            this.loadingEntitySearchWorkers = true
            try {
                const response = await Obra.byEntidade(entityId, {
                    obra_id: this.fields.obra_id
                })
                const rows = Array.isArray(response.data) ? response.data : []
                const selectedEntity = typeof entity === 'object'
                    ? entity
                    : this.entidades.find(item => String(item.ID) === String(entityId))

                this.entitySearchWorkers = rows.map(item => ({
                    id: item.ID,
                    ID: item.ID,
                    funcionario: item.ID,
                    numero: item.Numero || item.numero || '',
                    nome: item.Nome || item.nome || '',
                    entidade: item.entidade || selectedEntity || null,
                    hn: 8,
                    hext: 0,
                    local: null,
                    selected: false
                }))
            } catch (error) {
                this.entitySearchError = 'Não foi possível carregar os colaboradores desta entidade.'
            } finally {
                this.loadingEntitySearchWorkers = false
            }
        },

        async onEntitySearchFrenteChange() {
            this.entitySearchClasse = null
            this.entitySearchClasses = []

            if (this.hideFrentes) {
                this.entitySearchClasses = this.classes || []
                return
            }

            if (!this.entitySearchFrente) return

            const frente = this.frentes_obra.find(
                item => String(item.ID) === String(this.entitySearchFrente)
            )

            if (!frente) return

            if (Array.isArray(frente.classes) && frente.classes.length > 0) {
                this.entitySearchClasses = frente.classes
                return
            }

            const selectedWork = this.obras.find(
                item => String(item.Id) === String(this.fields.obra_id)
            )

            try {
                const response = await Classe.getClasses({
                    SubEmp: frente.SubEmp,
                    obra: selectedWork ? selectedWork.Codigo : null
                })
                this.entitySearchClasses = response.data || []
            } catch (error) {
                this.entitySearchError = 'Não foi possível carregar as classes da frente selecionada.'
            }
        },

        externalWorkerAlreadyAdded(worker) {
            const workerId = worker.ID || worker.id
            const workerNumber = worker.Numero || worker.numero

            return this.fields.funcionariosExternos.some(row => {
                const rowId = this.externalRowWorkerId(row)
                const rowNumber = row.numero || row.Numero

                if (rowId && workerId && String(rowId) === String(workerId)) return true
                if (rowNumber && workerNumber && String(rowNumber) === String(workerNumber)) return true

                return false
            })
        },

        externalRowWorkerId(row) {
            if (!row) return null

            if (row.ID) return row.ID

            if (row.funcionario && typeof row.funcionario === 'object') {
                return row.funcionario.ID || row.funcionario.id || null
            }

            if (row.funcionario && typeof row.funcionario !== 'object') {
                return row.funcionario
            }

            if (row.id && !String(row.id).includes('-new')) return row.id

            return null
        },

        toggleAllEntitySearchWorkers() {
            const nextValue = !this.allVisibleEntityWorkersSelected
            this.filteredEntitySearchWorkers.forEach(item => {
                item.selected = nextValue
            })
        },

        addEntitySearchWorkers() {
            this.entitySearchError = null

            if (!this.entitySearchEntity) {
                this.entitySearchError = 'Seleciona uma entidade.'
                return
            }

            if (!this.hideFrentes && !this.entitySearchFrente) {
                this.entitySearchError = 'Seleciona uma frente de obra.'
                return
            }

            if (this.entitySearchClasses.length > 0 && !this.entitySearchClasse) {
                this.entitySearchError = 'Seleciona uma classe.'
                return
            }

            const selected = this.entitySearchWorkers.filter(item => item.selected)

            if (selected.length === 0) {
                this.entitySearchError = 'Seleciona pelo menos um colaborador.'
                return
            }

            const entity = this.entidades.find(
                item => String(item.ID) === String(typeof this.entitySearchEntity === 'object' ? this.entitySearchEntity.ID : this.entitySearchEntity)
            ) || selected[0].entidade || null

            selected.forEach(item => {
                this.fields.funcionariosExternos.push({
                    id: item.ID || item.id,
                    ID: item.ID || item.id,
                    funcionario: item.ID || item.id,
                    numero: item.numero,
                    nome: item.nome,
                    entidade: entity,
                    frente_id: this.entitySearchFrente,
                    classe_id: this.entitySearchClasse,
                    hn: this.formatNumber(item.hn),
                    hext: this.formatNumber(item.hext),
                    local: item.local || null,
                    funcionariosEntidades: [],
                    options: ''
                })
            })

            this.$nextTick(() => {
                this.externalPage = this.externalTotalPages
            })

            this.closeEntitySearchDialog()
        },

        async openMoDialog(type, index = null) {
            this.moType = type
            this.moEditIndex = index
            this.moDialogError = null
            this.moExternalWorkers = []
            this.moAvailableClasses = []
            this.moClasseNeeded = false
            this.loadingMoCatalogs = true

            const list = type === 'internal'
                ? this.fields.funcionariosInternos
                : this.fields.funcionariosExternos

            const source = index === null ? null : list[index]

            this.moDraft = source
                ? this.clone(source)
                : {
                    id: `${dayjs().valueOf()}-new`,
                    funcionario: null,
                    entidade: null,
                    frente_id: null,
                    classe_id: null,
                    hn: 8,
                    hext: 0,
                    local: null,
                    options: ''
                }

            // Abrimos a modal imediatamente. Se o catálogo ainda estiver a ser
            // carregado em background, o utilizador vê o progress/loading em vez
            // de ficar com a sensação de que o botão não respondeu.
            this.moDialog = true

            try {
                if (type === 'internal') {
                    await this.ensureInternalCatalog()
                } else {
                    await this.ensureEntitiesCatalog()
                    if (this.moDraft.entidade) {
                        await this.loadExternalWorkers(this.moDraft.entidade, false)
                    } else if (Array.isArray(this.moDraft.funcionariosEntidades)) {
                        this.moExternalWorkers = this.moDraft.funcionariosEntidades
                    }
                }

                await this.prepareMoClasses()
            } catch (error) {
                this.moDialogError = 'Não foi possível carregar os dados necessários para editar esta linha.' 
            } finally {
                this.loadingMoCatalogs = false
            }
        },

        closeMoDialog() {
            this.moDialog = false
            this.moDialogError = null
        },

        async onMoEntityChange() {
            this.moDraft.funcionario = null
            await this.loadExternalWorkers(this.moDraft.entidade, true)
        },

        async loadExternalWorkers(entity, clearCurrent = false) {
            if (!entity) {
                this.moExternalWorkers = []
                return
            }

            const entityId = entity.ID || entity.id || entity
            this.loadingExternalWorkers = true

            try {
                const response = await Obra.byEntidade(entityId, { obra_id: this.fields.obra_id })
                this.moExternalWorkers = response.data || []
                if (clearCurrent) this.moDraft.funcionario = null
            } finally {
                this.loadingExternalWorkers = false
            }
        },

        async onMoFrenteChange() {
            this.moDraft.classe_id = null
            await this.prepareMoClasses()
        },

        async prepareMoClasses() {
            if (this.hideFrentes) {
                this.moAvailableClasses = this.classes || []
                this.moClasseNeeded = this.moAvailableClasses.length > 0
                return
            }

            if (!this.moDraft.frente_id) {
                this.moAvailableClasses = []
                this.moClasseNeeded = false
                return
            }

            const frente = this.frentes_obra.find(
                item => String(item.ID) === String(this.moDraft.frente_id)
            )

            if (!frente) {
                this.moAvailableClasses = []
                this.moClasseNeeded = false
                return
            }

            if (Array.isArray(frente.classes) && frente.classes.length > 0) {
                this.moAvailableClasses = frente.classes
            } else {
                const selectedWork = this.obras.find(
                    item => String(item.Id) === String(this.fields.obra_id)
                )

                const response = await Classe.getClasses({
                    SubEmp: frente.SubEmp,
                    obra: selectedWork ? selectedWork.Codigo : null
                })

                this.moAvailableClasses = response.data || []
            }

            this.moClasseNeeded = this.moAvailableClasses.length > 0
        },

        saveMoLine() {
            this.moDialogError = null

            if (!this.moDraft.funcionario) {
                this.moDialogError = 'Seleciona um funcionário.'
                return
            }

            if (!this.hideFrentes && !this.moDraft.frente_id) {
                this.moDialogError = 'Seleciona uma frente de obra.'
                return
            }

            if (this.moClasseNeeded && !this.moDraft.classe_id) {
                this.moDialogError = 'Seleciona uma classe.'
                return
            }

            const row = {
                ...this.clone(this.moDraft),
                hn: this.formatNumber(this.moDraft.hn),
                hext: this.formatNumber(this.moDraft.hext),
                classeNeeded: this.moClasseNeeded,
                available_classes: this.clone(this.moAvailableClasses)
            }

            if (this.moType === 'external') {
                row.funcionariosEntidades = this.clone(this.moExternalWorkers)
            }

            const list = this.moType === 'internal'
                ? this.fields.funcionariosInternos
                : this.fields.funcionariosExternos

            if (this.moEditIndex === null) {
                list.push(row)
            } else {
                this.$set(list, this.moEditIndex, row)
            }

            if (this.moType === 'internal') {
                this.$nextTick(() => {
                    this.internalPage = this.internalTotalPages
                })
            } else {
                this.$nextTick(() => {
                    this.externalPage = this.externalTotalPages
                })
            }

            this.closeMoDialog()
        },

        deleteFuncionarioInterno(index) {
            this.fields.funcionariosInternos.splice(index, 1)
        },

        deleteFuncionarioExterno(index) {
            this.fields.funcionariosExternos.splice(index, 1)
        },

        realInternalIndex(pageIndex) {
            return ((this.internalPage - 1) * this.internalPerPage) + pageIndex
        },

        realExternalIndex(pageIndex) {
            return ((this.externalPage - 1) * this.externalPerPage) + pageIndex
        },

        internalWorkerText(item) {
            const code = item ? item.funcionario : null
            const worker = this.funcionariosInternos.find(
                element => String(element.Codigo) === String(code)
            )

            if (worker) return `${worker.Codigo} - ${worker.Nome}`
            if (item && item.funcionario_nome) return `${code} - ${item.funcionario_nome}`
            if (item && item.nome) return `${code} - ${item.nome}`
            return this.valueOrDash(code)
        },

        externalWorkerText(item) {
            if (!item) return '-'

            const workers = item.funcionariosEntidades || []
            const worker = workers.find(element => {
                const id = element.ID || element.id
                const number = element.numero || element.Numero
                return String(id) === String(item.funcionario) || String(number) === String(item.funcionario)
            })

            if (worker) return this.externalCatalogWorkerText(worker)
            if (item.nome || item.Nome) {
                const number = item.numero || item.Numero || ''
                return `${number ? number + ' - ' : ''}${item.nome || item.Nome}`
            }

            if (typeof item.funcionario === 'object' && item.funcionario !== null) {
                return this.externalCatalogWorkerText(item.funcionario)
            }

            return this.valueOrDash(item.funcionario)
        },

        externalCatalogWorkerText(item) {
            if (!item) return '-'
            const number = item.numero || item.Numero || item.Codigo || ''
            const name = item.nome || item.Nome || item.name || ''
            return `${number ? number + ' - ' : ''}${name}` || '-'
        },

        entityText(entity) {
            if (!entity) return '-'
            if (typeof entity !== 'object') return entity
            const code = entity.Codigo || entity.codigo || ''
            const name = entity.Nome || entity.nome || entity.Descricao || ''
            return `${code ? code + ' - ' : ''}${name}` || '-'
        },

        frenteText(id) {
            if (!id) return this.hideFrentes ? '-' : '-'
            const frente = this.frentes_obra.find(item => String(item.ID) === String(id))
            return frente ? frente.Descricao : id
        },

        classeText(item) {
            if (!item || !item.classe_id) return '-'

            const localClasses = item.available_classes || []
            let classe = localClasses.find(element => String(element.ID) === String(item.classe_id))

            if (!classe) {
                classe = this.classes.find(element => String(element.ID) === String(item.classe_id))
            }

            if (!classe && item.frente_id) {
                const frente = this.frentes_obra.find(
                    element => String(element.ID) === String(item.frente_id)
                )
                if (frente && Array.isArray(frente.classes)) {
                    classe = frente.classes.find(
                        element => String(element.ID) === String(item.classe_id)
                    )
                }
            }

            return classe ? classe.Descricao : item.classe_id
        },

        valueOrDash(value) {
            return value === null || value === undefined || value === '' ? '-' : value
        },

        rowKey(item, index, prefix) {
            return `${prefix}-${item && item.id ? item.id : index}`
        },

        clone(value) {
            return JSON.parse(JSON.stringify(value))
        },

        async submit() {
            this.saving = true
            this.error = false
            this.error_message = null

            const data = {
                mo_internal: this.fields.funcionariosInternos,
                mo_external: this.fields.funcionariosExternos,
                eq_internal: this.fields.equipamentosInternos,
                eq_external: this.fields.equipamentosExternos,
                subEmpId: null,
                classeId: null,
                material: this.fields.materiais,
                subEmpreitadas: this.fields.subempreitadas,
                id: this.$route.params.id,
                work_code: this.fields.obra_id,
                user_id: this.fields.user_id
            }

            try {
                const action = this.method === 'update' ? 'edit' : this.method
                await Template[action](data, data.id)
                this.success = true

                setTimeout(() => {
                    this.$router.push('/configuracoes/templates')
                }, 500)
            } catch (err) {
                this.error = true
                this.error_message = err.response && err.response.data
                    ? err.response.data.message
                    : 'Ocorreu um erro ao gravar o template.'
            } finally {
                this.saving = false
            }
        }
    }
}
</script>
